// Blueprint plan view. Renders one rectangle per RoofSection with
// length (horizontal eaves) × eave-to-ridge (slope) labels. Sections lay
// out horizontally so the user can visually verify all geometries at
// once. Backward-compatible: if `sections` is missing it falls back to
// the legacy `measurements` prop.
import React from "react";

const BLUE = "#1E40AF";
const ORANGE = "#D94A31";
const TEXT = "#0F172A";

function legacyToSection(m = {}) {
  return {
    id: "legacy",
    name: "Roof",
    roof_type: "gable-to-gable",
    length: Number(m.length) || 8,
    eave_to_ridge: Number(m.width) ? Number(m.width) / 2 / Math.cos(((Number(m.pitch) || 35) * Math.PI) / 180) : 4.5,
    pitch: Number(m.pitch) || 35,
    overhang: Number(m.overhang) || 0.3,
    slope_coverage: "both",
  };
}

function SectionTile({ s, perCalc, ox, oy, scale }) {
  const length = Math.max(0.5, Number(s.length) || 0);
  const etr = Math.max(0.5, Number(s.eave_to_ridge) || 0);
  const w = length * scale;
  const h = etr * scale * 2; // visualised height = both slopes (mirrored about ridge)
  const x = ox;
  const y = oy;
  const cy = y + h / 2;
  const coverage = s.slope_coverage || "both";
  const isFront = coverage === "front" || coverage === "both";
  const isRear = coverage === "rear" || coverage === "both";

  return (
    <g>
      {/* outer rectangle (plan view) */}
      <rect x={x} y={y} width={w} height={h} stroke={BLUE} strokeWidth="2" fill="rgba(30,64,175,0.04)" />
      {/* ridge line */}
      <line x1={x} y1={cy} x2={x + w} y2={cy} stroke={ORANGE} strokeWidth="2.5" strokeDasharray="6 3" />

      {/* slope coverage indicator (light hatch on covered slopes) */}
      {isFront && <rect x={x} y={y} width={w} height={h / 2} fill="rgba(217,74,49,0.07)" />}
      {isRear && <rect x={x} y={cy} width={w} height={h / 2} fill="rgba(217,74,49,0.07)" />}

      {/* name label */}
      <text x={x + w / 2} y={y - 8} textAnchor="middle" fontFamily="Bricolage Grotesque, sans-serif" fontWeight="700" fontSize="12" fill={TEXT}>
        {s.name || "Section"}
      </text>

      {/* length annotation (bottom) */}
      <line x1={x} y1={y + h + 18} x2={x + w} y2={y + h + 18} stroke={ORANGE} strokeWidth="1" />
      <line x1={x} y1={y + h + 14} x2={x} y2={y + h + 22} stroke={ORANGE} strokeWidth="1" />
      <line x1={x + w} y1={y + h + 14} x2={x + w} y2={y + h + 22} stroke={ORANGE} strokeWidth="1" />
      <rect x={x + w / 2 - 36} y={y + h + 11} width="72" height="16" fill="#fff" stroke={ORANGE} strokeWidth="1" />
      <text x={x + w / 2} y={y + h + 22} textAnchor="middle" fontFamily="IBM Plex Mono, monospace" fontSize="10" fontWeight="700" fill={TEXT}>
        L {length.toFixed(2)} m
      </text>

      {/* E2R annotation (left side) */}
      <line x1={x - 20} y1={y} x2={x - 20} y2={y + h / 2} stroke={ORANGE} strokeWidth="1" />
      <line x1={x - 24} y1={y} x2={x - 16} y2={y} stroke={ORANGE} strokeWidth="1" />
      <line x1={x - 24} y1={y + h / 2} x2={x - 16} y2={y + h / 2} stroke={ORANGE} strokeWidth="1" />
      <g transform={`translate(${x - 30}, ${y + h / 4}) rotate(-90)`}>
        <rect x="-32" y="-8" width="64" height="16" fill="#fff" stroke={ORANGE} strokeWidth="1" />
        <text x="0" y="4" textAnchor="middle" fontFamily="IBM Plex Mono, monospace" fontSize="10" fontWeight="700" fill={TEXT}>
          E2R {etr.toFixed(2)} m
        </text>
      </g>

      {/* pitch chip */}
      <g transform={`translate(${x + w - 56}, ${y + 6})`}>
        <rect x="0" y="0" width="50" height="18" fill="#fff" stroke={BLUE} strokeWidth="1" />
        <text x="25" y="13" textAnchor="middle" fontFamily="IBM Plex Mono, monospace" fontSize="10" fontWeight="700" fill={BLUE}>
          {Number(s.pitch || 0)}°
        </text>
      </g>

      {/* coverage chip */}
      <g transform={`translate(${x + 4}, ${y + 6})`}>
        <rect x="0" y="0" width="46" height="18" fill={ORANGE} />
        <text x="23" y="13" textAnchor="middle" fontFamily="IBM Plex Mono, monospace" fontSize="9" fontWeight="700" fill="#fff">
          {coverage.toUpperCase()}
        </text>
      </g>

      {/* per-section area readout */}
      {perCalc && (
        <text x={x + w / 2} y={cy + 4} textAnchor="middle" fontFamily="IBM Plex Mono, monospace" fontSize="10" fontWeight="700" fill={BLUE}>
          {perCalc.roof_area_m2?.toFixed(1)} m²
        </text>
      )}
    </g>
  );
}

export default function RoofPlanSVG({ sections, perSection, measurements, roofTypeId }) {
  const list = sections && sections.length ? sections : [legacyToSection(measurements || {})];

  // Layout: simple horizontal flow.  Compute per-section width on a shared
  // metric scale so big sections render bigger than small ones.
  const maxL = Math.max(...list.map((s) => Number(s.length) || 1), 1);
  const maxE = Math.max(...list.map((s) => Number(s.eave_to_ridge) || 1), 1);
  const tileMaxW = 220;
  const tileMaxH = 140;
  const scale = Math.min(tileMaxW / maxL, tileMaxH / (maxE * 2));

  const tilePad = 80;
  const tileWidths = list.map((s) => Math.max(60, (Number(s.length) || 0.5) * scale));
  const tileHeights = list.map((s) => Math.max(60, (Number(s.eave_to_ridge) || 0.5) * scale * 2));
  const rowH = Math.max(...tileHeights, 100);
  const totalW = tileWidths.reduce((a, b) => a + b, 0) + (list.length - 1) * tilePad + 120;
  const totalH = rowH + 100;

  let cx = 60;
  return (
    <div className="blueprint-grid border border-slate-300 rounded-sm overflow-x-auto">
      <svg
        viewBox={`0 0 ${Math.max(totalW, 600)} ${totalH}`}
        className="w-full h-auto block"
        preserveAspectRatio="xMidYMid meet"
        style={{ minHeight: 240 }}
      >
        {list.map((s, i) => {
          const w = tileWidths[i];
          const h = tileHeights[i];
          const y = (totalH - h) / 2;
          const tile = (
            <SectionTile
              key={s.id || i}
              s={s}
              perCalc={(perSection || [])[i]}
              ox={cx}
              oy={y}
              scale={scale}
            />
          );
          cx += w + tilePad;
          return tile;
        })}
      </svg>
    </div>
  );
}
